import styled from 'styled-components';

// Default design sizes (grommet text tokens): 3xl = 32px / 48px line-height for
// the whole-dollar part, medium = 14px / 24px line-height for the decimal part.
export const DEFAULT_INTEGER_FONT_SIZE = 32;
export const DEFAULT_INTEGER_LINE_HEIGHT = 48;
export const DEFAULT_DECIMAL_FONT_SIZE = 14;
export const DEFAULT_DECIMAL_LINE_HEIGHT = 24;

// Used for any amount longer than the largest breakpoint below (grommet xlarge).
const SMALLEST_INTEGER_FONT_SIZE = 18;
const SMALLEST_INTEGER_LINE_HEIGHT = 27;

interface Breakpoint {
  /**
   * Maximum length (number of characters) of the formatted whole-dollar string
   * — including the `$` sign and grouping commas, excluding the decimals — for
   * which this integer font size is used.
   */
  maxLength: number;
  integerFontSize: number;
  /** Line height (px) paired with the font size, sourced from the grommet theme. */
  integerLineHeight: number;
}

/**
 * Number-based font-size breakpoints (EL-33170).
 *
 * The size is chosen purely from how many characters the formatted amount has,
 * NOT from the viewport or container width. Below the first threshold the
 * default 32px is kept; each larger magnitude steps the size down so the full
 * value fits inside the fixed-width balance column without truncation.
 *
 * Font size / line height pairs come from the grommet theme text tokens:
 * - 32px / 48px -> `3xl`
 * - 28px / 40px -> interpolated between `2xl` (24/32) and `3xl` (32/48)
 * - 24px / 32px -> `2xl`
 * - 20px / 32px -> `xxlarge`
 * - 18px / 27px -> `xlarge`
 *
 * Reference lengths (whole-dollar part, incl. `$` and commas):
 * - `$999,999`       -> 8 chars
 * - `$9,999,999`     -> 10 chars
 * - `$57,823,338`    -> 11 chars
 * - `$1,234,567,890` -> 14 chars
 */
const BREAKPOINTS: Breakpoint[] = [
  { maxLength: 8, integerFontSize: 32, integerLineHeight: 48 },
  { maxLength: 10, integerFontSize: 28, integerLineHeight: 40 },
  { maxLength: 12, integerFontSize: 24, integerLineHeight: 32 },
  { maxLength: 14, integerFontSize: 20, integerLineHeight: 32 },
];

const getBreakpoint = (formatted: string): Breakpoint =>
  BREAKPOINTS.find((item) => formatted.length <= item.maxLength) ?? {
    maxLength: Infinity,
    integerFontSize: SMALLEST_INTEGER_FONT_SIZE,
    integerLineHeight: SMALLEST_INTEGER_LINE_HEIGHT,
  };

/**
 * Picks the whole-dollar font size (px) for a formatted amount string based on
 * its character length. Pure and framework-free so it can be unit tested.
 */
export const getIntegerFontSize = (formatted: string): number => getBreakpoint(formatted).integerFontSize;

/** Line height (px) paired with the whole-dollar font size for the breakpoint. */
export const getIntegerLineHeight = (formatted: string): number => getBreakpoint(formatted).integerLineHeight;

/**
 * Scales the decimal font size by the same ratio applied to the integer part,
 * preserving the design proportion (32px / 14px).
 */
export const getDecimalFontSize = (integerFontSize: number): number =>
  Math.round(DEFAULT_DECIMAL_FONT_SIZE * (integerFontSize / DEFAULT_INTEGER_FONT_SIZE));

/**
 * Scales the decimal line height by the same ratio as the decimal font size,
 * preserving the design proportion (14px / 24px).
 */
export const getDecimalLineHeight = (integerFontSize: number): number =>
  Math.round(DEFAULT_DECIMAL_LINE_HEIGHT * (integerFontSize / DEFAULT_INTEGER_FONT_SIZE));

interface AmountWrapperProps {
  /** Formatted whole-dollar string; its length drives the font-size breakpoint. */
  $formatted: string;
}

/**
 * Wraps the amount on a single line and applies the number-based font size and
 * matching line height to the whole-dollar part.
 */
export const AmountValue = styled.span<AmountWrapperProps>`
  display: inline-block;
  white-space: nowrap;
  font-size: ${({ $formatted }) => getIntegerFontSize($formatted)}px;
  line-height: ${({ $formatted }) => getIntegerLineHeight($formatted)}px;
`;

/** Decimal part, scaled down proportionally to the whole-dollar part. */
export const AmountDecimal = styled.span<AmountWrapperProps>`
  font-size: ${({ $formatted }) => getDecimalFontSize(getIntegerFontSize($formatted))}px;
  line-height: ${({ $formatted }) => getDecimalLineHeight(getIntegerFontSize($formatted))}px;
`;
